import React, { type ReactNode, useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import findIndex from 'lodash/findIndex';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	DragDropContext as DragDropContextDI,
	Draggable as DraggableDI,
	Droppable as DroppableDI,
	type DropResult,
	type ResponderProvided,
	type DroppableProvidedProps,
} from 'react-beautiful-dnd';
import { token } from '@atlaskit/tokens';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DragAndDropQueuesContainerBorderCompiled } from '@atlassian/jira-servicedesk-common/src/styles.tsx';
import { useCategorizedNavItems } from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import { QueueItem as QueueItemDI } from './queue-item/main.tsx';
import type {
	QueueListDraggableProps,
	QueueListProps,
	QueueListDraggableWithOrderingProps,
} from './types.tsx';

export const QueueList = ({ QueueItem = QueueItemDI, queues, section }: QueueListProps) => (
	<>
		{/* @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'ComponentType<QueueItemProps>'. */}
		{queues.map<typeof QueueItem>((queue) => (
			<QueueItem key={queue.id} queue={queue} section={section} />
		))}
	</>
);

export const QueueListDraggable = ({
	DragDropContext = DragDropContextDI,
	Droppable = DroppableDI,
	Draggable = DraggableDI,
	QueueItem = QueueItemDI,
	onDragEnd,
	queues,
}: QueueListDraggableProps) => {
	const [isDragging, setIsDragging] = useState(false);
	const onDragStart = useCallback(() => {
		setIsDragging(true);
	}, []);

	const onDragEndInternal = useCallback(
		(result: DropResult, provided: ResponderProvided) => {
			setIsDragging(false);
			onDragEnd(result, provided);
		},
		[onDragEnd],
	);

	const renderQueueItems = useCallback(
		(droppableProps: DroppableProvidedProps, placeholder: ReactNode) => (
			<>
				{queues.map((queue, index) => (
					<Draggable
						disableInteractiveElementBlocking
						key={queue.id}
						draggableId={queue.id}
						index={index}
					>
						{({ innerRef, dragHandleProps, draggableProps }) => (
							<QueueItem
								// @ts-expect-error - TS2322 - Type '{ style?: DraggingStyle | NotDraggingStyle | undefined; 'data-rbd-draggable-context-id': string; 'data-rbd-draggable-id': string; onTransitionEnd?: TransitionEventHandler<...> | undefined; ... 8 more ...; queue: QueueNavItem; }' is not assignable to type 'IntrinsicAttributes & QueueItemProps & { children?: ReactNode; }'.
								ref={innerRef}
								queue={queue}
								{...dragHandleProps}
								{...draggableProps}
							/>
						)}
					</Draggable>
				))}
				{placeholder}
			</>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queues],
	);

	return (
		<DragDropContext onDragStart={onDragStart} onDragEnd={onDragEndInternal}>
			<Droppable droppableId="queues">
				{({ innerRef: droppableRef, droppableProps, placeholder }, { isDraggingOver }) => (
					<DraggableQueueListContainer
						isDragging={isDragging}
						isDraggingOver={isDraggingOver}
						ref={droppableRef}
						{...droppableProps}
					>
						{renderQueueItems(droppableProps, placeholder)}
					</DraggableQueueListContainer>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export const QueueListDraggableWithOrdering = ({
	practice,
	queues,
	...rest
}: QueueListDraggableWithOrderingProps) => {
	// TODO This baseUrl has a dependency o@atlassian/jira-servicedesk-queues-categorized-store
	// The above items need to be updated prior to complete baseUrl removal from this file.
	const baseUrl = '';
	const { data: project } = useProject();
	const projectKey = project ? project.key : '';
	const [allQueues, , , methods] = useCategorizedNavItems(baseUrl, projectKey, practice);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleDragEnd = useCallback(
		(data: DropResult) => {
			const destinationIndex = data.destination?.index;
			if (data && data.source && destinationIndex !== undefined && allQueues && queues) {
				const fromIndex = findIndex(allQueues, { id: data.draggableId });
				const sourceIndexQueue = queues[destinationIndex];
				const toIndex = findIndex(allQueues, { id: sourceIndexQueue.id });
				methods.moveItem(fromIndex, toIndex);

				const analyticsEvent = createAnalyticsEvent({
					action: 'dragged',
					actionSubject: 'navigationItem',
				});

				fireUIAnalytics(analyticsEvent, {
					itemId: `queue-${sourceIndexQueue.id}`,
					itemType: 'queue',
				});
			}
		},
		[allQueues, createAnalyticsEvent, methods, queues],
	);
	return <QueueListDraggable {...rest} queues={queues} onDragEnd={handleDragEnd} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DraggableQueueListContainer = styled(DragAndDropQueuesContainerBorderCompiled)(
	{
		marginLeft: token('space.025'),

		marginRight: token('space.025'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDragging }: { isDragging: boolean }) => !isDragging && 'background-color: unset',
);
